import React from 'react';
import { ListItem, Typography, Icon, Divider, Collapse, List, createStyles, makeStyles, Theme } from '@material-ui/core';

import { PageLocation } from '../../../shared/enum/PageLocation';

interface IMenuItemComponentProps {
    principalItemTitle: string,
    toggleDrawer: (open: boolean, pageLocation: PageLocation | null) => (event: React.KeyboardEvent | React.MouseEvent) => void,
    isOpen?: boolean,
    openPrincipalItem?: () => void,
    onClickPrincipalItem?: (event: React.KeyboardEvent | React.MouseEvent) => void,
    children?: IMenuItemChildrenProps[]
}

interface IMenuItemChildrenProps {
    title: string,
    pageLocation: PageLocation,
    show: boolean,
    event?: any,
    customAction?: () => void
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        nested: {
            paddingLeft: theme.spacing(4)
        },
        dropArrows: {
            color: theme.palette.primary.light,
        },
        listItem: {
            justifyContent: 'space-between',
            alignItems: 'center'
        },
        scrollableSubmenu: {
            maxHeight: '28rem',
            overflowY: 'auto',
            overflowX: 'hidden'
        }
    }),
);

function MenuItemComponent({ principalItemTitle, toggleDrawer, isOpen, openPrincipalItem, onClickPrincipalItem, children }: IMenuItemComponentProps) {
    const classes = useStyles();

    const getPrincipalItemArrows = () => {
        if (children && children.length > 0) {
            return (
                <Icon className={classes.dropArrows}>
                    {isOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                </Icon>
            );
        }
    };

    const getPrincipalMenuItem = () => {
        return (
            <ListItem
                button
                onClick={openPrincipalItem || onClickPrincipalItem}
                className={classes.listItem}>
                <Typography variant="body2">
                    {principalItemTitle}
                </Typography>
                {getPrincipalItemArrows()}
            </ListItem>
        );
    };

    const getChildMenuItem = (show: boolean, pageLocation: PageLocation, childItemTitle: string, event: any, customAction?: () => void) => {
        if (customAction) {
            return (
                <ListItem
                    button
                    className={classes.nested}
                    onClick={customAction}>
                    <Typography variant="body2">
                        {childItemTitle}
                    </Typography>
                </ListItem>
            );
        }
        if (event) {
            return (
                <ListItem
                    button
                    className={classes.nested}
                    onClick={() => event()}>
                    <Typography variant="body2">
                        {childItemTitle}
                    </Typography>
                </ListItem>
            );
        }
        if (show && !event) {
            return (
                <ListItem
                    button
                    className={classes.nested}
                    onClick={toggleDrawer(false, pageLocation)}>
                    <Typography variant="body2">
                        {childItemTitle}
                    </Typography>
                </ListItem>
            );
        }
    };

    const getChildrenItems = () => {
        if (children && children.length > 0) {
            const shouldScroll = children.length > 5;
            return (
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <List
                        component="div"
                        disablePadding
                        className={shouldScroll ? classes.scrollableSubmenu : undefined}>
                        {children.map((child: IMenuItemChildrenProps, index) => (
                            <React.Fragment key={index}>
                                <Divider />
                                {getChildMenuItem(child.show, child.pageLocation, child.title, child.event, child.customAction)}
                            </React.Fragment>
                        ))}
                    </List>
                </Collapse>
            );
        }
    };

    return (
        <div>
            {getPrincipalMenuItem()}
            {getChildrenItems()}
        </div>
    );
}

export default React.memo(
    MenuItemComponent,
    (p, n) => (p.isOpen === n.isOpen && p.principalItemTitle === n.principalItemTitle)
);